import axiosInstanceNode, { LONG_REQUEST_TIMEOUT_MS } from "@/api/axiosInstanceNode";

export const createCampaign = async (campaignData) => {
  try {
    const response = await axiosInstanceNode.post("/campaign/create", campaignData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to create campaign. Please try again.",
    );
  }
};

export const getCampaigns = async (
  pageNum = 1,
  limitNum = 10,
  searchQuery = "",
) => {
  try {
    const params = new URLSearchParams({
      page: pageNum.toString(),
      limit: limitNum.toString(),
    });
    if (searchQuery) params.append("search", searchQuery);

    const response = await axiosInstanceNode.get(
      `/campaign/my?${params.toString()}`,
    );
    const campaignsArray =
      response?.data?.data?.data || response?.data?.data || [];
    const totalCount =
      response?.data?.totalCount || response?.data?.data?.totalCount || 0;
    return { campaigns: campaignsArray || [], totalCount, page: pageNum, limit: limitNum };
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to get campaigns. Please try again.",
    );
  }
};

export const getCampaignById = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.get(`/campaign/${campaignId}`);
    const body = response.data;
    if (body?.data != null && typeof body.data === "object") {
      return body.data;
    }
    return body;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to get campaign. Please try again.",
    );
  }
};

export const updateCampaign = async (campaignId, campaignData) => {
  try {
    const response = await axiosInstanceNode.patch(
      `/campaign/${campaignId}`,
      campaignData,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to update campaign. Please try again.",
    );
  }
};

export const duplicateCampaign = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.post(`/campaign/${campaignId}/duplicate`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to duplicate campaign. Please try again.",
    );
  }
};

/** GET - the campaign file (setup only, no leads or senders) that Download saves. */
export const exportCampaign = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.get(`/campaign/${campaignId}/export`);
    return response.data?.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to download campaign. Please try again.",
    );
  }
};

/** POST - creates a draft campaign from a downloaded campaign file. */
export const importCampaign = async (campaignFile) => {
  try {
    const response = await axiosInstanceNode.post("/campaign/import", campaignFile);
    return response.data?.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to import campaign. Please try again.",
    );
  }
};

export const getCampaignIntegrations = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.get(`/campaign/${campaignId}/integrations`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to get campaign integrations. Please try again.",
    );
  }
};

export const updateCampaignIntegrations = async (campaignId, payload) => {
  try {
    const response = await axiosInstanceNode.put(
      `/campaign/${campaignId}/integrations`,
      payload,
    );
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update campaign integrations. Please try again.",
    );
  }
};

export const getCampaignReadiness = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.get(`/campaign/${campaignId}/readiness`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to check campaign readiness. Please try again.",
    );
  }
};

export const updateCampaignNotificationEmail = async (
  campaignId,
  notificationEmail,
) => {
  try {
    const response = await axiosInstanceNode.put(
      `/campaign/${campaignId}/notification-email`,
      { notificationEmail: notificationEmail ?? "" },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update notification email. Please try again.",
    );
  }
};

export const deleteCampaign = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.delete(`/campaign/${campaignId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to delete campaign. Please try again.",
    );
  }
};

export const bulkDeleteCampaigns = async (campaignIds) => {
  try {
    const response = await axiosInstanceNode.post("/campaign/bulk-delete", {
      campaignIds,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to delete campaigns. Please try again.",
    );
  }
};


export const fetchCampaignIndividualSequencingContent = async ({
  campaignId,
  prospectId,
  forceRegenerate = false,
}) => {
  try {
    const response = await axiosInstanceNode.post(
      `/campaign/sequence-content`,
      {
        campaignId,
        contactId: prospectId,
        forceRegenerate: Boolean(forceRegenerate),
      },
      // LLM generation: must outlive the instance's default timeout.
      { timeout: LONG_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to get campaign individual sequencing. Please try again.",
    );
  }
};

/** GET — content-writer cache freshness for a single lead (drives new_update UX). */
export const getContentWriterStatus = async ({ campaignId, contactId }) => {
  try {
    const params = new URLSearchParams({ contact_id: String(contactId) });
    const response = await axiosInstanceNode.get(
      `/campaign/${campaignId}/content-writer-status?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to get content writer status. Please try again.",
    );
  }
};

export const updateCampaignIndividualSequencingContent = async ({
  campaignId,
  prospectId,
  newsContent,
  selectedSignalTrigger,
  selectedSignalTheme,
}) => {
  try {
    const response = await axiosInstanceNode.post(
      `/campaign/sequence-content-refine`,
      {
        campaignId,
        contactId: prospectId,
        news: newsContent,
        selectedSignalTrigger,
        selectedSignalTheme,
      },
      // LLM refinement: must outlive the instance's default timeout.
      { timeout: LONG_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to update campaign individual sequencing content. Please try again.",
    );
  }
};

export const addCampaignAsset = async ({
  assetData,
  description,
  onUploadProgress,
}) => {
  try {
    let fileToUpload;
    if (assetData instanceof File) {
      fileToUpload = assetData;
    } else if (assetData?.file instanceof File) {
      fileToUpload = assetData.file;
    } else {
      throw new Error("Invalid file. Please select a valid file.");
    }
    if (!fileToUpload) throw new Error("File is required");

    const formData = new FormData();
    formData.append("file", fileToUpload);
    if (description) formData.append("context", description);

    const response = await axiosInstanceNode.post(`/campaign/assets`, formData, {
      timeout: 60000,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onUploadProgress(percentCompleted);
        }
      },
    });
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to add campaign asset. Please try again.";
    throw new Error(errorMessage);
  }
};

export const deleteCampaignAsset = async (assetId) => {
  try {
    const response = await axiosInstanceNode.delete(
      `/campaign/remove-asset/${assetId}`,
    );
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to delete campaign asset. Please try again.";
    throw new Error(errorMessage);
  }
};

export const getAnalyticsAggregatedDataForCampaignDashboard = async (
  campaignId,
  from,
  to,
  scope = "current",
) => {
  try {
    const response = await axiosInstanceNode.get(
      `/campaign/${campaignId}/analytics`,
      { params: { from, to, scope } },
    );
    return response.data?.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to get analytics aggregated data for campaign dashboard. Please try again.",
    );
  }
};

export const getAnalyticsAggregatedDataForCampaignDashboardByChannel = async (
  campaignId,
  action,
  channel,
  from,
  to,
  scope = "current",
) => {
  try {
    const response = await axiosInstanceNode.get(
      `/campaign/${campaignId}/analytics/contacts`,
      { params: { action, channel, from, to, scope } },
    );
    return response.data?.data;
    
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to get analytics aggregated data for campaign dashboard by channel. Please try again.",
    );
  }
};

// campaigns start/stop/resume
export const campaignStatusAction = async ({ campaignId, action, payload }) => {
  try {
    const response = await axiosInstanceNode.post(
      `/campaign/${campaignId}/${action}`,
      payload || {},
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      `Failed to ${action} campaign. Please try again.`,
    );
  }
};

/** GET — enrollment/content readiness (allLeadsReady, counts, per-lead results). */
export const getCampaignContentStatus = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.get(
      `/campaign/${campaignId}/content-status`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to get campaign content status. Please try again.",
    );
  }
};

/** POST — generate missing step content for all enrollments (can take a while). */
export const generateMissingCampaignContent = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.post(
      `/campaign/${campaignId}/generate-missing-content`,
      {},
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to generate missing content. Please try again.",
    );
  }
};

export const getSequenceTransitions = async (fromStepId) => {
  try {
    const response = await axiosInstanceNode.get(
      "/campaign/sequence-transitions",
      { params: { fromStepId } },
    );
    return response.data || [];
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to get campaign branches. Please try again.",
    );
  }
};

export const saveSequenceFallback = async ({ fromStepId, toStepId }) => {
  try {
    const response = await axiosInstanceNode.post(
      "/campaign/sequence-transitions",
      {
        fromStepId,
        toStepId: toStepId || null,
        fallback: true,
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to save campaign branch. Please try again.",
    );
  }
};

export const deleteSequenceTransition = async (transitionId) => {
  try {
    const response = await axiosInstanceNode.delete(
      `/campaign/sequence-transitions/${transitionId}`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to reset campaign branch. Please try again.",
    );
  }
};

export {
  getCampaignSteps,
  createCampaignStep,
  updateCampaignStep,
  deleteCampaignStep,
  reorderCampaignSteps,
  updateCampaignStepAdvanceSettings,
  updateCampaignStepContentUserChanges,
} from "./campaignStepsApi";

export {
  addLeadsToCampaign,
  getCampaignLeads,
  deleteLead,
} from "./enrollmentsApi";
